import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { saveAs } from "file-saver";

import { toast } from "react-toastify";

import Footer from "../../common/components/Footer";
import Nav from "../../common/components/Nav";
import ScrollReveal from "../../common/components/ScrollReveal";
import { fetchPublicPagesByType } from "../../utils/api/cms/page";
import { errorHandler } from "../../utils/helpers/errorHandler";

import DownloadApp from "../../common/components/DownloadApp";
const token = JSON.parse(localStorage.getItem("eko-360-p2-token"));
const rootUrl = `${process.env.REACT_APP_ROOT_URL}`;

const DataSetsPage = () => {
  const [publicPages, setPublicPages] = useState([]);
  const [publicDatasets, setPublicDatasets] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const handleFetchPublicDatasets = async (page = 0) => {
    try {
      setLoading(true);
      const data = await fetchPublicPagesByType(page, "datasets");

      setPublicDatasets(data.data.allPages);
    } catch (error) {
      errorHandler(error, toast);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchPublicDatasets();
  }, []);

  useEffect(() => {
    if (publicDatasets) {
      const datasets = publicDatasets.filter((pages) => pages.type === "datasets");

      setDatasets(datasets);
      setFiltered(datasets);
    }
  }, [publicDatasets]);

  /* Real-time filter on title */
  useEffect(() => {
    if (search.trim() === "") {
      setFiltered(datasets);
    } else {
      setFiltered(
        datasets.filter((dataset) =>
          (dataset.title || "").toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, datasets]);

  const handleFileDownload = async (event, dataset) => {
    event.preventDefault();
    try {
      const fileName = dataset.file.substr(dataset.file.lastIndexOf("/") + 1);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/datasets/${fileName}/download/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      );

      const blob = await res.blob();
      saveAs(blob, dataset.title);
    } catch (error) {
      return toast.error(`An unexpected error just occured ${error.message}`);
    }
  };

  /* Banners are normally bare filenames in /uploads, but a leading slash
     means the value is already a full server path. */
  const getBannerUrl = (banner) => {
    if (!banner) return null;

    return banner.startsWith("/")
      ? `${rootUrl}/${banner.substr(banner.lastIndexOf("/") + 1)}`
      : `${rootUrl}/uploads/${banner}`;
  };

  if (loading) {
    return (
      <div>
        <Nav />
        <div className="container text-center py-5">
          <div className="spinner-border" role="status" style={{ color: "#0C9EA9" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading datasets...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <header>
        <Nav />
      </header>

      {/* Hero Banner */}
      <ScrollReveal>
        <section
          style={{
            background: "linear-gradient(135deg, #0C9EA9 0%, #076f78 100%)",
            padding: "80px 0 60px",
          }}
        >
          <div className="container text-center text-white">
            <span
              style={{
                display: "inline-block",
                background: "rgba(255,255,255,0.15)",
                borderRadius: "20px",
                padding: "4px 16px",
                fontSize: "12px",
                fontWeight: "600",
                letterSpacing: "1.2px",
                marginBottom: "16px",
                textTransform: "uppercase",
              }}
            >
              Open Data Repositories
            </span>
            <h1
              className="fw-bold mb-3"
              style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}
            >
              Open Datasets
            </h1>
            <p
              className="mb-4"
              style={{ opacity: 0.85, maxWidth: "560px", margin: "0 auto 24px" }}
            >
              Explore and download the latest datasets across health, education and
              economic affairs.
            </p>
            <div
              className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <span style={{ fontSize: "1.4rem", fontWeight: "700" }}>
                {datasets.length}
              </span>
              <span style={{ opacity: 0.85, fontSize: "0.9rem" }}>
                Dataset{datasets.length !== 1 ? "s" : ""} Available
              </span>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Search Bar */}
      <ScrollReveal>
        <section
          style={{
            background: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
            padding: "20px 0",
          }}
        >
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6 col-md-8">
                <div className="input-group">
                  <span
                    className="input-group-text bg-white border-end-0"
                    style={{ borderColor: "#dee2e6" }}
                  >
                    <i className="fa fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0"
                    placeholder="Search datasets by title..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ borderColor: "#dee2e6", boxShadow: "none" }}
                  />
                  {search && (
                    <button
                      className="btn btn-outline-secondary border-start-0"
                      onClick={() => setSearch("")}
                      style={{ borderColor: "#dee2e6" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* ── SECTION: Dataset Cards ── */}
      <ScrollReveal>
        <section className="py-5" style={{ background: "#ffffff" }}>
          <div className="container">
            {filtered.length > 0 ? (
              <div className="row g-4">
                {filtered.map((dataset, idx) => {
                  const bannerUrl = getBannerUrl(dataset?.banner);

                  return (
                    <div
                      className="col-xl-4 col-md-6 d-flex"
                      key={dataset.uuid || idx}
                    >
                      <div className="h-100 w-100">
                        <ScrollReveal className="h-100 w-100">
                          <div
                            className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white d-flex flex-column"
                            style={{
                              transition: "transform 0.25s ease, box-shadow 0.25s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-6px)";
                              e.currentTarget.style.boxShadow =
                                "0 16px 40px rgba(0,0,0,0.13)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "";
                            }}
                          >
                            {bannerUrl && (
                              <div
                                style={{
                                  height: "160px",
                                  backgroundImage: `url(${bannerUrl})`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                }}
                              />
                            )}
                            <div  />
                            <div className="card-body p-4 d-flex flex-column">
                              <h5 className="fw-bold text-dark mb-2 fs-6">
                                {dataset.title}
                              </h5>
                              <div
                                className="text-black small flex-grow-1 mb-3"
                                dangerouslySetInnerHTML={{
                                  __html: dataset.body,
                                }}
                              />
                              {dataset.file && (
                                <button
                                  onClick={(e) => handleFileDownload(e, dataset)}
                                  className="btn btn-sm text-white rounded-2 w-100 fw-semibold mt-auto"
                                  style={{
                                    backgroundColor: "#0C9EA9",
                                    border: "none",
                                  }}
                                >
                                  <i className="fa fa-download me-2"></i> Download
                                  Dataset
                                </button>
                              )}
                            </div>
                          </div>
                        </ScrollReveal>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-5">
                <div style={{ fontSize: "3rem", marginBottom: "16px" }}>📊</div>
                <h4 className="text-dark fw-bold">
                  {search ? "No results found" : "No datasets available"}
                </h4>
                <p className="text-muted">
                  {search
                    ? `No datasets match "${search}". Try a different search term.`
                    : "There are no datasets available at the moment."}
                </p>
                {search && (
                  <button
                    className="btn btn-outline-secondary mt-2"
                    onClick={() => setSearch("")}
                  >
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </ScrollReveal>

      {/* Back to Home */}
      <ScrollReveal>
        <section className="py-4 bg-light border-top">
          <div className="container text-center">
            <Link
              to="/"
              className="btn btn-outline-dark rounded-pill px-4 fw-semibold"
            >
              ← Back to Homepage
            </Link>
          </div>
        </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
};

export default DataSetsPage;
